import { stringify } from 'query-string';
import { HttpClient } from 'api/http-client';
import { User } from 'api/models';
import { Field } from 'api/tasks';

export enum NoteSourceType {
  Company = 'COMPANY',
  Project = 'PROJECT',
  ExpenseReject = 'EXPENSE_REJECT',
  ExpenseReportReject = 'EXPENSE_REPORT_REJECT',
}

export enum NoteSubType {
  ProjectReject = 'PROJECT_REJECT',
  WorkReject = 'WORK_REJECT',
  OverallReject = 'OVERALL_REJECT',
  CompanyField = 'COMPANY_FIELD',
  ProjectField = 'PROJECT_FIELD',
  ExpenseReject = 'EXPENSE_REJECT',
}

export interface NoteSource {
  sourceId: number;
  sourceType: NoteSourceType;
  subSourceTypes?: NoteSourceType[];
}
export interface Note {
  noteId: number;
  noteText: string;
  isPinned: boolean;
  noteTitle?: string;
  field: Field;
  createdBy: number;
  createdByUser?: User;
  createdAt: number;
  updatedBy?: number;
  updatedByUser?: User;
  updatedAt?: number;
  tagIds?: number[];
  objectId?: number;
  objectType?: string;
  sourceName?: string;
  sourceId?: number;
  noteType: string;
  subType?: NoteSubType;
}
export interface CreateNotePayload {
  note: Pick<Note, 'noteTitle' | 'noteText'> & { subObjectIds?: number[] };
  associatedEntity: NoteSource;
  field: {
    fieldId: number;
  };
}
export interface UpdateNotePayload {
  note: Pick<
    Note,
    'noteId' | 'noteTitle' | 'noteText' | 'isPinned' | 'noteType' | 'sourceId'
  > & {
    subObjectIds?: number[];
  };
}

export type DeleteNotePayload = Pick<Note, 'noteId' | 'noteType' | 'sourceId'>;

class NoteHttpClient extends HttpClient {
  public getAllNotesBySource = async (source: NoteSource): Promise<Note[]> =>
    this.get<Note[]>(`/notes?${stringify(source)}`);

  public createNote = async (payload: CreateNotePayload): Promise<Note> =>
    this.post<Note, CreateNotePayload>('/notes', payload);

  public updateNote = async (payload: UpdateNotePayload): Promise<Note> =>
    this.put<Note, UpdateNotePayload>(`/notes/${payload.note.noteId}`, payload);

  public deleteNote = async (noteId: number): Promise<void> =>
    this.delete(`/notes/${noteId}`);

  /** Project Note Endpoints */
  public getAllProjectNotes = async (source: NoteSource): Promise<Note[]> =>
    this.get<Note[]>(`/notes/projects/${source.sourceId}?${stringify(source)}`);

  public createProjectNote = async (
    payload: CreateNotePayload,
  ): Promise<Note> =>
    this.post<Note, CreateNotePayload>(
      `/notes/projects/${payload.associatedEntity.sourceId}`,
      payload,
    );

  public updateProjectNote = async (
    payload: UpdateNotePayload,
    sourceType: NoteSourceType,
    sourceId: number,
  ): Promise<Note> =>
    this.put<Note, UpdateNotePayload>(
      `/notes/${payload.note.noteId}/projects/${sourceId}/`,
      payload,
    );

  public deleteProjectNote = async (
    noteId: number,
    sourceType: NoteSourceType,
    sourceId: number,
  ): Promise<void> => this.delete(`/notes/${noteId}/projects/${sourceId}`);
}
export const NoteAPI = new NoteHttpClient();
